import { type MutableRefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { produce } from 'immer'
import { useTranslation } from 'react-i18next'
import { type InfiniteData, useQueryClient } from 'react-query'
import { Swiper, type SwiperClass, SwiperSlide } from 'swiper/react'

import { useStoryPollOptimistic } from 'api/stories/useStoryPollOptimistic/useStoryPollOptimistic'
import placeholderAvatar from 'assets/images/user_placeholder.png'
import { IconPostPlayOutline } from 'assets/svg/sprite'
import AvatarHolder from 'components/UI/AvatarHolder/AvatarHolder'
import { getStoryBackground } from 'components/UI/BackgroundPicker/BackgroundPicker'
import TipModal from 'components/UI/Modal/Tip/TipModal'
import { PostPoll, PostStorySidebar } from 'components-ref/molecules'
import { useGlobalVideo } from 'context/globalVideoContext'
import { useModalContext } from 'context/modalContext'
import VideoSurface from 'context/VideoSurface'
import CaptionText from 'features/PhotoEdit/CaptionText/CaptionText'
import { useAnswerPoll, useStoryLike, useStorySeen } from 'helpers/hooks'
import { getCachedBlob } from 'helpers/mediaPrefetch'
import { useCachedStoryImage } from 'helpers/useCachedStoryImage'
import { someTimeAgo } from 'lib/dayjs'
import {
  STILL_DURATION_MS,
  getStillElapsedMs,
  pauseStillClock,
  resetStillClock,
  resumeStillClock
} from 'services/stories/storyStillClock/storyStillClock'
import { useUserProfile } from 'store'
import { usePreviewStore } from 'store/previewStore'
import { type Story } from 'types/api/story'
import { type IInfinitePage } from 'types/interfaces/ITypes'

import 'swiper/css'

import styles from './postStory.module.scss'

interface Props {
  storyData: Story[]
  dataQuery: readonly any[]
  initialSlide: number
  avatarUrl: string | null
  author: string
  statsAmount: number
  hideHeader?: boolean
  isPreview?: boolean
  setActiveStorySlide?: (_idx: number) => void
  fullscreenHandoffRef?: MutableRefObject<boolean>
  toggleStats: () => void
  postId: string
}

export interface IProgress {
  id: string
  duration: number
  seen: boolean
}

interface IProgressBarProps {
  progressList: IProgress[]
  activeIndex: number
  activeFillRef: MutableRefObject<HTMLDivElement | null>
  isPlaying: boolean
  hasStartedStory: boolean
  allStoriesSeen: boolean
}

const clampProgress = (value: number) => Math.max(0, Math.min(1, value))

export const ProgressBar = ({
  progressList,
  activeIndex,
  activeFillRef,
  isPlaying,
  hasStartedStory,
  allStoriesSeen
}: IProgressBarProps) => {
  return (
    <div className={styles.progressBar}>
      {progressList.map((story, index) => {
        const isActive = index === activeIndex

    const progress = !hasStartedStory && allStoriesSeen
    ? 1
    : hasStartedStory
        ? index < activeIndex
        ? 1
        : 0
        : isPlaying && isActive
        ? 0
        : story.seen
            ? 1
            : 0

        return (
            <div
            key={story.id}
            className={`${styles.progress} ${
                isActive ? styles.active : ''
            } ${
                hasStartedStory
                ? index < activeIndex
                    ? styles.passed
                    : ''
                : story.seen
                    ? styles.passed
                    : ''
            }`}
            >
            <div
              ref={isActive ? activeFillRef : null}
              className={styles.progressFill}
              style={{
                transform: `scaleX(${progress})`
              }}
            />
          </div>
        )
      })}
    </div>
  )
}

export const PostStory = ({
postId,
  storyData,
  dataQuery,
  initialSlide,
  avatarUrl,
  author,
  statsAmount,
  hideHeader = false,
  isPreview = false,
  setActiveStorySlide,
  fullscreenHandoffRef,
  toggleStats
}: Props) => {

  const [activeSlide, setActiveSlide] = useState(initialSlide)
  const [autoplay, setAutoplay] = useState(false)
  const [videoPlaying, setVideoPlaying] = useState(false)
  const [isInViewport, setIsInViewport] = useState(false)
    const [hasStartedStory, setHasStartedStory] = useState(false)

  const swiperRef = useRef<SwiperClass | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const manifestObserverRef = useRef<IntersectionObserver | null>(null)
  const progressFillRef = useRef<HTMLDivElement | null>(null)
  const autoStartStoryRef = useRef<string | null>(null)
  const didAutoAlignToUnseenRef = useRef(false)

const {
  activeMediaId,
  setActiveMediaId,
  play,
  pause,
  getVideoElement,
} = useGlobalVideo()

  const previewOpen = usePreviewStore(state => state.previewOpen)
  const previewStoryPostId = usePreviewStore(
    state =>
        state.state.selectedPostType === 'story'
        ? state.selectedPostData?.id
        : null
    )

    const previewStorySlide = usePreviewStore(
    state => state.state.selectedSlide
    )
  const userData = useUserProfile()
  const modal = useModalContext()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { mutate: answerStoryPoll } = useStoryPollOptimistic()

  const activeStory = storyData[activeSlide]
  const activeStoryThumb = useCachedStoryImage(
    activeStory?.image_src_thumb ??
    activeStory?.video_thumb_src ??
    null
    )
  const activeVideoId = activeStory?.video_src ? `story:${activeStory.id}` : null
  
  const ownsVideo = Boolean(activeVideoId && activeMediaId === activeVideoId && !previewOpen)

    const allStoriesSeen =
    storyData.length > 0 &&
    storyData.every(story => story.seen)

    const progressList = useMemo<IProgress[]>(() => (
    storyData.map(story => ({
        id: story.id,
        duration: story.video_src ? (story.duration || 7) : 7,
        seen: story.seen
    }))
    ), [storyData])

  const cachedData = queryClient.getQueryData<InfiniteData<IInfinitePage>>(dataQuery)

    useLayoutEffect(() => {

    if (!previewOpen) return
    if (previewStoryPostId !== postId) return
    if (
    typeof previewStorySlide !== 'number' ||
    !Number.isInteger(previewStorySlide) ||
    previewStorySlide < 0 ||
    previewStorySlide >= storyData.length
    ) {
    return
    }

    // Fullscreen playback is an active story session.
    // Pausing or closing fullscreen must not revert to seen-status mode.
    setHasStartedStory(true)

    const swiper = swiperRef.current
    if (!swiper || swiper.destroyed) return

    if (swiper.activeIndex !== previewStorySlide) {
    swiper.slideTo(previewStorySlide, 0)
    }
    }, [
    previewOpen,
    previewStoryPostId,
    previewStorySlide,
    postId
    ])


  const { storySeenMutation } = useStorySeen({
    onMutate: (vars: any) => {
      const updated = produce(cachedData, draft => {
        draft?.pages.forEach(page => {
          page.page.data.data.forEach(post => {
            const story = post.stories?.find(item => item.id === vars.storyId)
            if (story && !story.seen) story.seen = true
          })
        })
      })
      queryClient.setQueryData(dataQuery, updated)
    },
    onError: () => queryClient.setQueryData(dataQuery, cachedData)
  })

  const { storyLikeMutation } = useStoryLike({
    onMutate: (vars: any) => {
      const updated = produce(cachedData, draft => {
        draft?.pages.forEach(page => {
          page.page.data.data.forEach(post => {
            const story = post.stories?.find(item => item.id === vars.storyId)
            if (!story) return
            story.liked = !story.liked
            story.like_count += story.liked ? 1 : -1
          })
        })
      })
      queryClient.setQueryData(dataQuery, updated)
    },
    onError: () => queryClient.setQueryData(dataQuery, cachedData)
  })

  const markSeen = (story: Story) => {
    if (!story.seen) storySeenMutation.mutate({ storyId: story.id })
  }

  // Direct DOM updates avoid rerendering the entire story/Swiper tree per frame.
  const paintProgress = useCallback((fraction: number) => {
    if (progressFillRef.current) {
      progressFillRef.current.style.transform = `scaleX(${clampProgress(fraction)})`
    }
  }, [])

useLayoutEffect(() => {
  const swiper = swiperRef.current
  if (!swiper) return

    const progressBars = (swiper.el as HTMLElement)
    .closest(`.${styles.box}`)
    ?.querySelectorAll<HTMLElement>(`.${styles.progressFill}`)

  if (!progressBars) return

  progressBars.forEach((bar, index) => {
    if (index === activeSlide && (hasStartedStory || !allStoriesSeen)) {
    return
    }
    const story = storyData[index]

    const shouldBeFull = !hasStartedStory && allStoriesSeen
    ? true
    : hasStartedStory
        ? index < activeSlide
        : Boolean(story?.seen)

    bar.style.transform = `scaleX(${shouldBeFull ? 1 : 0})`
  })
}, [activeSlide, storyData, hasStartedStory, allStoriesSeen])


  const advanceStory = useCallback(() => {
    const swiper = swiperRef.current
    if (!swiper) return
    const current = storyData[swiper.activeIndex]
    if (current && !current.seen) storySeenMutation.mutate({ storyId: current.id })
    if (swiper.isEnd) {
      setAutoplay(false)
    } else {
      swiper.slideNext()
    }
  }, [storyData, storySeenMutation])

  const viewportRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect()
    manifestObserverRef.current?.disconnect()
    if (!node) return
    manifestObserverRef.current = new IntersectionObserver(
      entries => setIsInViewport(Boolean(entries[0]?.isIntersecting)),
      { rootMargin: '350px 0px', threshold: 0 }
    )
    manifestObserverRef.current.observe(node)
    observerRef.current = new IntersectionObserver(entries => {
      const entry = entries[0]
      if (!entry) return
      if (!entry.isIntersecting) {
        setAutoplay(false)
        return
      }
      const hasUnseen = storyData.some(story => !story.seen)
    const stillHasTime =
    activeStory &&
    !activeStory.video_src &&
    getStillElapsedMs(activeStory.id) < STILL_DURATION_MS

    if (!swiperRef.current?.isEnd || (hasUnseen && stillHasTime)) {
    setAutoplay(true)
    }
    }, { threshold: 1 })
    observerRef.current.observe(node)
  }, [storyData, activeStory?.duration])

  useEffect(() => () => {
    observerRef.current?.disconnect()
    manifestObserverRef.current?.disconnect()
  }, [])

  // Images and polls use a pause-aware monotonic clock. Videos use the native video clock below.

    useEffect(() => {
    if (!activeStory || activeStory.video_src) return

    const storyId = activeStory.id

    const paintCurrentProgress = () => {
        paintProgress(
        getStillElapsedMs(storyId) / STILL_DURATION_MS
        )
    }

    // Fresh load: all stories watched, no navigation yet.
    // Keep the active photo's progress bar completely full.
    if (!previewOpen && !hasStartedStory && allStoriesSeen) {
        paintProgress(1)
        return
    }

    // Don't run the inline clock while idle or in fullscreen.
    if (!autoplay || previewOpen) {
        return
    }

    // Restore the current elapsed position.
    paintCurrentProgress()

    resumeStillClock(storyId, 'inline')

    let frameId = 0

    const tick = () => {
        const elapsedMs = getStillElapsedMs(storyId)

        paintProgress(elapsedMs / STILL_DURATION_MS)

        if (elapsedMs >= STILL_DURATION_MS) {
        pauseStillClock(storyId, 'inline')
        advanceStory()
        return
        }

        frameId = requestAnimationFrame(tick)
    }

    frameId = requestAnimationFrame(tick)

    return () => {
        cancelAnimationFrame(frameId)
        pauseStillClock(storyId, 'inline')
    }
    }, [
    activeStory,
    autoplay,
    previewOpen,
    hasStartedStory,
    allStoriesSeen,
    paintProgress,
    advanceStory
    ])

  // Match the native video-frame tracking used by PostVideo's progress bar.
  // This effect only observes playback; it never starts or seeks the video.
  useEffect(() => {
    if (!activeVideoId || !activeStory?.video_src) {
      setVideoPlaying(false)
      return
    }

    const video = getVideoElement(activeVideoId, activeStory.video_src)
    if (!video) return

    let frameId: number | null = null
    let videoFrameId: number | null = null
    let running = false

    const isActiveFeedVideo = () =>
      !previewOpen &&
      isInViewport &&
      activeMediaId === activeVideoId &&
      swiperRef.current?.activeIndex === activeSlide

    const renderTime = (time: number) => {
      if (!isActiveFeedVideo()) return

      const duration =
        Number.isFinite(video.duration) && video.duration > 0
          ? video.duration
          : activeStory.duration || 1

      paintProgress(time / duration)
    }

    const sync = () => renderTime(video.currentTime)

    const runRaf = () => {
      if (!running) return
      sync()
      frameId = requestAnimationFrame(runRaf)
    }

    const runVideoFrame = (_now: DOMHighResTimeStamp, metadata: VideoFrameCallbackMetadata) => {
      if (!running) return
      renderTime(metadata.mediaTime)
      videoFrameId = video.requestVideoFrameCallback(runVideoFrame)
    }

    const stop = () => {
      running = false

      if (frameId !== null) {
        cancelAnimationFrame(frameId)
        frameId = null
      }

      if (videoFrameId !== null && 'cancelVideoFrameCallback' in video) {
        video.cancelVideoFrameCallback(videoFrameId)
        videoFrameId = null
      }

      sync()
    }

    const start = () => {
      if (running || video.paused || video.ended || !isActiveFeedVideo()) return
      running = true
      sync()

      if ('requestVideoFrameCallback' in video) {
        videoFrameId = video.requestVideoFrameCallback(runVideoFrame)
      } else {
        frameId = requestAnimationFrame(runRaf)
      }
    }

    const onPlay = () => {
      setVideoPlaying(true)
      start()
    }

    const onPause = () => {
      setVideoPlaying(false)
      stop()
    }

    const onEnded = () => {
      stop()
      setVideoPlaying(false)
      if (!isActiveFeedVideo()) return

      paintProgress(1)
      advanceStory()
    }

    video.addEventListener('play', onPlay)
    video.addEventListener('playing', onPlay)
    video.addEventListener('pause', onPause)
    video.addEventListener('ended', onEnded)
    video.addEventListener('seeked', sync)
    video.addEventListener('loadedmetadata', sync)
    video.addEventListener('durationchange', sync)

    setVideoPlaying(!video.paused && !video.ended)
    sync()
    start()

    return () => {
      stop()
      video.removeEventListener('play', onPlay)
      video.removeEventListener('playing', onPlay)
      video.removeEventListener('pause', onPause)
      video.removeEventListener('ended', onEnded)
      video.removeEventListener('seeked', sync)
      video.removeEventListener('loadedmetadata', sync)
      video.removeEventListener('durationchange', sync)
    }
  }, [
    activeVideoId,
    activeStory,
    activeSlide,
    activeMediaId,
    isInViewport,
    previewOpen,
    getVideoElement,
    paintProgress,
    advanceStory
  ])

  useEffect(() => {
    if (!activeVideoId || !activeStory?.video_src) return
    if (!isInViewport || previewOpen || activeMediaId !== activeVideoId) return
    if (autoStartStoryRef.current !== activeStory.id) return
    autoStartStoryRef.current = null
    void play(activeVideoId, activeStory.video_src)
  }, [activeVideoId, activeStory, activeMediaId, isInViewport, previewOpen, play])

  // Only the feed relinquishes playback when it leaves the viewport.
  useEffect(() => {
    if (!activeVideoId || activeMediaId !== activeVideoId) return
    if (previewOpen || fullscreenHandoffRef?.current) return
    if (!isInViewport) {
      pause()
      setActiveMediaId(null)
    }
  }, [activeVideoId, activeMediaId, isInViewport, previewOpen, fullscreenHandoffRef, pause, setActiveMediaId])

  const playStoryVideo = (story: Story) => {
    if (!story.video_src) return
    const mediaId = `story:${story.id}`
    const video = getVideoElement(mediaId, story.video_src)
    if (video?.ended) video.currentTime = 0
    setActiveMediaId(mediaId)
    void play(mediaId, story.video_src)
  }

  const handleSlideChange = (swiper: SwiperClass) => {
    const nextIndex = swiper.activeIndex
    const nextStory = storyData[nextIndex]
    const isRealInlineSlideChange =
    !previewOpen &&
    didAutoAlignToUnseenRef.current &&
    swiper.previousIndex !== swiper.activeIndex

    if (!nextStory) return

    if (isRealInlineSlideChange) {
    setHasStartedStory(true)
    }
    const previous = storyData[swiper.previousIndex]
    if (previous && !previous.seen) markSeen(previous)
    // A real inline navigation starts the next still story fresh.
    // Fullscreen synchronization must preserve its elapsed time.
if (isRealInlineSlideChange && !nextStory.video_src) {
  resetStillClock(nextStory.id)
}

    setActiveSlide(nextIndex)
    setActiveStorySlide?.(nextIndex)

    paintProgress(
    nextStory.video_src
        ? 0
        : getStillElapsedMs(nextStory.id) / STILL_DURATION_MS
    )
    if (!isInViewport || previewOpen) return
    if (nextStory.video_src) {
    const mediaId = `story:${nextStory.id}`

    // A genuine inline slide change restarts the selected video.
    // Fullscreen synchronization returns earlier, preserving its position.
    const video = getVideoElement(mediaId, nextStory.video_src)

    if (video) {
        video.currentTime = 0
    }

    autoStartStoryRef.current = nextStory.id
    setActiveMediaId(mediaId)
    } else {
    autoStartStoryRef.current = null
    setActiveMediaId(null)
    }
  }

const handleSwiperInit = (swiper: SwiperClass) => {
  swiperRef.current = swiper

  const firstUnseenIndex = storyData.findIndex(story => !story.seen)

  const nextIndex =
    firstUnseenIndex >= 0
      ? firstUnseenIndex
      : swiper.activeIndex

  if (nextIndex !== swiper.activeIndex) {
    swiper.slideTo(nextIndex, 0)
  }

  setActiveSlide(nextIndex)
  setActiveStorySlide?.(nextIndex)

  // Initial Swiper positioning is not story navigation.
  setHasStartedStory(false)

  didAutoAlignToUnseenRef.current = true
}
  useEffect(() => {
    if (!storyData.length || !swiperRef.current || didAutoAlignToUnseenRef.current) return
    const index = storyData.findIndex(story => !story.seen)
    if (index < 0) return
    didAutoAlignToUnseenRef.current = true
    swiperRef.current.slideTo(index, 0)
  }, [storyData])

  const renderStory = (story: Story, index: number) => {

    const { liked, like_count } = story

    const isActive = index === activeSlide

    const thumbnail = isActive
    ? activeStoryThumb
    : getCachedBlob(
        story.image_src_thumb ??
        story.video_thumb_src ??
        ''
        )
    const pollBackgroundImage = story.background_color
    ? getStoryBackground(story.background_color)
    : thumbnail || story.image_src_thumb || story.image_src
        ? `url("${thumbnail || story.image_src_thumb || story.image_src}")`
        : undefined

    const modelData = {
      storyId: String(story.id),
      postId: story.post_id,
      modelId: story.user_id,
      avatarSrc: story.user?.avatar?.url || placeholderAvatar
    }
    const sidebar = (
      <PostStorySidebar
        isEnabled={true}
        isOwner={userData?.id === story.user_id}
        showStats={userData?.role === 'model'}
        amount={statsAmount}
        liked={liked}
        likeCount={like_count}
        toggleStats={toggleStats}
        toggleLiked={() => storyLikeMutation.mutate({ storyId: story.id })}
        onTipModal={() => {
            modal.addModal(
                t('tipAmount'),
                <TipModal tipType='post' modelData={modelData} />,
                false,
                false,
                '',
                '',
                true // keyboardAdjust
            )
        }}
        modelData={modelData}
      />
    )
    const caption = story.text?.value ? (
      <CaptionText
        text={story.text.value}
        xPercent={story.text.x ?? 50}
        yPercent={story.text.y ?? 50}
      />
    ) : null
    if (story.image_src && !story.poll) {
      return (
        <div className={styles.storySlide}>
          {sidebar}
            {thumbnail && <img src={thumbnail} alt='' />}
          {caption}
        </div>
      )
    }
    if (story.video_src) {
      return (
        <div className={styles.storySlide}>
          {sidebar}
          <VideoSurface
            ownerType='story'
            ownerId={`story:${story.id}`}
            mediaId={`story:${story.id}`}
            url={story.video_src}
            cloudfront={story.video_cloudfront}
            poster={thumbnail ?? undefined}
            active={ownsVideo && index === activeSlide}
            isFeedHost
            className='story-video-surface'
          />
          {index === activeSlide && !videoPlaying && !previewOpen && (
            <button
              type='button'
              className={styles.storyPlayButton}
              aria-label='Play story video'
              onClick={event => {
                event.stopPropagation()
                playStoryVideo(story)
              }}
            >
              <IconPostPlayOutline />
            </button>
          )}
        </div>
      )
    }
    return (
      <div className={styles.storySlide}>
        {sidebar}
        <div
          className='post__files__file post__files__file--poll post__files__file--story-poll'
          style={{
            backgroundImage: pollBackgroundImage
          }}
        >
          {story.poll && (
            <PostPoll
              pollData={story.poll}
            setAnswer={answer => {
            answerStoryPoll({
                ...answer,
                storyId: story.id
            })
            }}
              isMyPost={false}
            />
          )}
          {caption}
        </div>
      </div>
    )
  }

  if (!storyData.length) return null

  return (
    <div ref={viewportRef} className={`${styles.box} ${isPreview ? styles.preview : ''}`}>
    <ProgressBar
    progressList={progressList}
    activeIndex={activeSlide}
    activeFillRef={progressFillRef}
    isPlaying={
        !previewOpen &&
        isInViewport &&
        (
        activeStory?.video_src
            ? videoPlaying
            : autoplay
        )
    }
    hasStartedStory={hasStartedStory}
    allStoriesSeen={allStoriesSeen}
    />
      {!hideHeader && (
        <div className={styles.header}>
          <div className={styles.avatar}>
            <AvatarHolder img={avatarUrl ?? placeholderAvatar} size='36' />
          </div>
          <div className={styles.content}>
            <div className={styles.author}>{author}</div>
            <div className={styles.time}>
            {activeStory ? someTimeAgo(activeStory.created_at) : ''}
            </div>
          </div>
        </div>
      )}
      <div className={styles.container}>
        <Swiper
          loop={false}
          resistance={true}
          touchReleaseOnEdges={true}
          onInit={handleSwiperInit}
          onSlideChange={handleSlideChange}
          speed={100}
          initialSlide={activeSlide}
          className={styles.storySlider}
        >
          {storyData.map((story, index) => (
            <SwiperSlide key={story.id}>
              {renderStory(story, index)}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  )
}
